import { Stack } from '@mui/material';
import React, { useEffect, useState } from 'react';
import { ServiceLocation } from 'src/services/serviceLocationsAxios';
import CountWidget from 'src/views/dashboards/widgets/counterWidget';
import { useTheme } from '@mui/material';
import { useAuth } from 'react-oidc-context';
import { useQueryParams } from 'src/@core/hooks/useQueryParams';
import { useRouter } from 'next/router';
import { useVigoTranslation } from 'src/@core/hooks/useVigoTranslation';
import { useQuery } from '@tanstack/react-query';
import { GetAxiosInstance } from 'src/services/baseService';
import { SalesOrderMetricsDto } from 'src/models/salesorder';
import { subscribe } from 'src/@core/utils/vigoCustomEvent';

interface OrderListProps {
  filter?: string;
  onSelect?: (filter: string) => any;
}

const OrderListFilters = (props: OrderListProps) => {
  const { filter, onSelect } = props;

  const [labelNotPrinted, setLabelNotPrinted] = useState<boolean>(false);
  const [discrepancies, setDiscrepancies] = useState<boolean>(false);
  const [labelPrinted, setLabelPrinted] = useState<boolean>(false);
  const [paperworkAssigned, setPaperworkAssigned] = useState<boolean>(false);
  const [outstanding, setOutstanding] = useState<boolean>(false);
  const [completed, setCompleted] = useState<boolean>(false);
  const [totalOrders, setTotalOrders] = useState<boolean>(false);

  const [notPrintedCount, setNotPrintedCount] = useState<number>(0);
  const [printedCount, setPrintedCount] = useState<number>(0);
  const [discrepCount, setDiscrepCount] = useState<number>(0);
  const [paperworkCount, setPaperworkCount] = useState<number>(0);
  const [outstandingCount, setOutstandingCount] = useState<number>(0);
  const [completedCount, setCompletedCount] = useState<number>(0);
  const [totalCount, setTotalCount] = useState<number>(0);

  const { t } = useVigoTranslation();
  const auth = useAuth();
  const user = auth.user;
  const loggedInUserId = user?.profile.tenantUserId as string;

  const theme = useTheme();

  const { removeQueryParam } = useQueryParams();
  const router = useRouter();
  const { extrafilter = undefined } = router.query;

  const { refetch } = useQuery({
    queryKey: [`filter`, filter, ServiceLocation.salesorder],
    queryFn: async () => {
      const params = { filter: filter, showDeleted: filter?.includes('deletedOn') ? true : false };

      // reset all counts
      setNotPrintedCount(0);
      setPrintedCount(0);
      setDiscrepCount(0);
      setPaperworkCount(0);
      setOutstandingCount(0);
      setCompletedCount(0);
      setTotalCount(0);

      const ret = await GetAxiosInstance().get<SalesOrderMetricsDto>(
        `${ServiceLocation.salesorder}/${'SalesOrder/metrics'}`,
        {
          params
        }
      );

      return ret.data;
    },
    enabled: Boolean(filter),
    refetchOnWindowFocus: false,
    onSuccess: data => {
      // set not printed
      const labelNotPrintedCount = (data.total ?? 0) - (data.labelPrinted ?? 0);
      // count discrepancies
      const DiscrepanciesCount = data.discrepancies ?? 0;
      // count label printed
      const labelPrintedCount = data.labelPrinted ?? 0;
      // count paperwork assigned
      const paperworkAttachedCount = data.paperworkAttached ?? 0;
      // count Outstanding
      const latestStatusCount = (data.total ?? 0) - (data.completed ?? 0);
      // count Completed
      const completedCount = data.completed ?? 0;
      // count Total Orders
      const totalOrdersCount = data.total ?? 0;

      setNotPrintedCount(labelNotPrintedCount);
      setPrintedCount(labelPrintedCount);
      setDiscrepCount(DiscrepanciesCount);
      setPaperworkCount(paperworkAttachedCount);
      setOutstandingCount(latestStatusCount);
      setCompletedCount(completedCount);
      setTotalCount(totalOrdersCount);
    }
  });

  const clickMe = (additionalFilter: string, selected: boolean): void => {
    removeQueryParam('extrafilter');
    if (onSelect) {
      onSelect(selected ? additionalFilter.toString() : '');
      localStorage.setItem('orderlist-ordderFilter-' + loggedInUserId, selected ? additionalFilter.toString() : '');
    }
  };

  const handleFilter = (filter: string) => {
    switch (true) {
      case filter.toLowerCase().includes('labelprinted eq false'):
        setLabelNotPrinted(true);
        setDiscrepancies(false);
        setLabelPrinted(false);
        setPaperworkAssigned(false);
        setOutstanding(false);
        setCompleted(false);
        setTotalOrders(false);
        break;

      case filter.toLowerCase().includes('labelprinted eq true'):
        setLabelPrinted(true);
        setDiscrepancies(false);
        setLabelNotPrinted(false);
        setPaperworkAssigned(false);
        setOutstanding(false);
        setCompleted(false);
        setTotalOrders(false);
        break;

      case filter.toLowerCase().includes('hasdiscrepancies eq true'):
        setDiscrepancies(true);
        setLabelNotPrinted(false);
        setLabelPrinted(false);
        setPaperworkAssigned(false);
        setOutstanding(false);
        setCompleted(false);
        setTotalOrders(false);
        break;

      case filter.toLowerCase().includes('paperworkattached eq true'):
        setPaperworkAssigned(true);
        setDiscrepancies(false);
        setLabelNotPrinted(false);
        setLabelPrinted(false);
        setOutstanding(false);
        setCompleted(false);
        setTotalOrders(false);
        break;

      case filter.toLowerCase().includes("lateststatus eq 'delivered'"):
        setPaperworkAssigned(false);
        setDiscrepancies(false);
        setLabelNotPrinted(false);
        setLabelPrinted(false);
        setOutstanding(false);
        setCompleted(true);
        setTotalOrders(false);
        break;

      case filter.toLowerCase().includes("lateststatus ne 'delivered'"):
        setPaperworkAssigned(false);
        setDiscrepancies(false);
        setLabelNotPrinted(false);
        setLabelPrinted(false);
        setOutstanding(true);
        setCompleted(false);
        setTotalOrders(false);
        break;

      case filter.toLowerCase().includes('isdeleted eq false'):
        setPaperworkAssigned(false);
        setDiscrepancies(false);
        setLabelNotPrinted(false);
        setLabelPrinted(false);
        setOutstanding(false);
        setCompleted(false);
        setTotalOrders(true);
        break;

      default:
        setPaperworkAssigned(false);
        setDiscrepancies(false);
        setLabelNotPrinted(false);
        setLabelPrinted(false);
        setOutstanding(false);
        setCompleted(false);
        setTotalOrders(false);
        break;
    }
  };

  useEffect(() => {
    const handler = () => {
      refetch();
    };
    const unsubscribe = subscribe('refetchFilters', handler);

    const savedFilter = localStorage.getItem('orderlist-ordderFilter-' + loggedInUserId);

    if (extrafilter) {
      handleFilter(extrafilter as string);
      if (onSelect) {
        onSelect(extrafilter ? (extrafilter as string) : '');
      }
    } else {
      handleFilter(savedFilter ?? '');

      if (onSelect) {
        onSelect(savedFilter ? savedFilter : '');
      }
    }

    return () => {
      unsubscribe();
    };

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <Stack sx={{ padding: '0px', backgroundColor: theme.palette.background.default }} direction={'row'} spacing={3}>
      <CountWidget
        name="notprinted"
        countOverride={notPrintedCount}
        filter={filter ? filter + '  and LabelPrinted eq false' : 'LabelPrinted eq false'}
        subFilter={'LabelPrinted eq false'}
        title={t('Not Printed')}
        onSelect={() => {
          clickMe('labelPrinted eq false', !labelNotPrinted);
          setLabelNotPrinted(!labelNotPrinted);
          setDiscrepancies(false);
          setLabelPrinted(false);
          setPaperworkAssigned(false);
          setOutstanding(false);
          setCompleted(false);
          setTotalOrders(false);
        }}
        selected={labelNotPrinted}
      />

      <CountWidget
        name="labelprinted"
        countOverride={printedCount}
        filter={filter ? filter + '  and LabelPrinted eq true' : 'LabelPrinted eq true'}
        subFilter={'LabelPrinted eq true'}
        title={t('Printed')}
        onSelect={() => {
          clickMe('labelPrinted eq true', !labelPrinted);
          setLabelPrinted(!labelPrinted);
          setDiscrepancies(false);
          setLabelNotPrinted(false);
          setPaperworkAssigned(false);
          setOutstanding(false);
          setCompleted(false);
          setTotalOrders(false);
        }}
        selected={labelPrinted}
      />

      <CountWidget
        name="discrepancies"
        countOverride={discrepCount}
        filter={filter ? filter + ' and HasDiscrepancies eq true' : 'HasDiscrepancies eq true'}
        subFilter={'HasDiscrepancies eq true'}
        title={t('Discrepancies')}
        onSelect={() => {
          clickMe('HasDiscrepancies eq true', !discrepancies);
          setDiscrepancies(!discrepancies);
          setLabelNotPrinted(false);
          setLabelPrinted(false);
          setPaperworkAssigned(false);
          setOutstanding(false);
          setCompleted(false);
          setTotalOrders(false);
        }}
        selected={discrepancies}
      />

      <CountWidget
        name="paperworkassigned"
        countOverride={paperworkCount}
        filter={filter ? filter + ' and paperworkAttached eq true' : 'paperworkAttached eq true'}
        subFilter={'paperworkAttached eq true'}
        title={t('Paperwork')}
        subtitle="Attached"
        onSelect={() => {
          clickMe('paperworkAttached eq true', !paperworkAssigned);
          setDiscrepancies(false);
          setLabelNotPrinted(false);
          setLabelPrinted(false);
          setPaperworkAssigned(!paperworkAssigned);
          setOutstanding(false);
          setCompleted(false);
          setTotalOrders(false);
        }}
        selected={paperworkAssigned}
      />

      <CountWidget
        name="outstanding"
        countOverride={outstandingCount}
        filter={filter ? filter + " and latestStatus ne 'Delivered'" : "latestStatus ne 'Delivered'"}
        subFilter={"latestStatus ne 'Delivered'"}
        title={t('Outstanding')}
        onSelect={() => {
          clickMe("latestStatus ne 'Delivered'", !outstanding);
          setOutstanding(!outstanding);
          setDiscrepancies(false);
          setLabelPrinted(false);
          setPaperworkAssigned(false);
          setLabelNotPrinted(false);
          setCompleted(false);
          setTotalOrders(false);
        }}
        selected={outstanding}
      />

      <CountWidget
        name="completed"
        filter={filter ? filter + " and latestStatus eq 'Delivered'" : "latestStatus eq 'Delivered'"}
        countOverride={completedCount}
        subFilter={"latestStatus eq 'Delivered'"}
        title={t('Completed')}
        onSelect={() => {
          clickMe("latestStatus eq 'Delivered'", !completed);
          setCompleted(!completed);
          setDiscrepancies(false);
          setLabelPrinted(false);
          setPaperworkAssigned(false);
          setOutstanding(false);
          setLabelNotPrinted(false);
          setTotalOrders(false);
        }}
        selected={completed}
      />

      <CountWidget
        name="totalorders"
        countOverride={totalCount}
        title={t('Total Orders')}
        filter={filter}
        subFilter={''}
        onSelect={() => {
          clickMe('', !totalOrders);
          setTotalOrders(!totalOrders);
          setDiscrepancies(false);
          setLabelPrinted(false);
          setPaperworkAssigned(false);
          setOutstanding(false);
          setCompleted(false);
          setLabelNotPrinted(false);
        }}
        selected={totalOrders}
      />
    </Stack>
  );
};

export default OrderListFilters;
